import React, { useState } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Icon, IconButton } from '@openedx/paragon';
import { ContentCopy } from '@openedx/paragon/icons';

import messages from '../messages';
import { getStudioBlock, StudioBlock } from '../data/api';

// ─── Studio Block Panel ───────────────────────────────────────────────────────

interface StudioBlockPanelProps {
  configId: string;
  content: 'configuration' | 'report';
}

interface CopyableConfigFieldProps {
  label: string;
  value: string;
  fieldId: string;
  isCopied: boolean;
  onCopy: (fieldId: string, value: string) => void;
  code?: boolean;
}

const CopyableConfigField: React.FC<CopyableConfigFieldProps> = ({
  label,
  value,
  fieldId,
  isCopied,
  onCopy,
  code = false,
}) => (
  <div className="lti-tools-tab__field">
    <label className="form-label lti-tools-tab__field-label">{label}</label>
    <div className="lti-tools-tab__copyable-field">
      <input
        className={`form-control lti-tools-tab__compact-control ${code ? 'lti-tools-tab__code-control' : ''}`}
        readOnly
        value={value}
      />
      <IconButton
        src={ContentCopy}
        iconAs={Icon}
        alt={`Copy ${label}`}
        onClick={() => onCopy(fieldId, value)}
      />
    </div>
    {isCopied && <span className="lti-tools-tab__copy-confirmation" role="status">Copied to clipboard!</span>}
  </div>
);

const StudioBlockPanel: React.FC<StudioBlockPanelProps> = ({ configId, content }) => {
  const intl = useIntl();
  const [block, setBlock] = useState<StudioBlock | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  React.useEffect(() => {
    setLoading(true);
    setError(null);
    getStudioBlock(configId)
      .then(setBlock)
      .catch(() => setError(intl.formatMessage(messages.studioPanelErrorMessage)))
      .finally(() => setLoading(false));
  }, [configId]);

  const handleCopy = async (fieldId: string, value: string) => {
    let copied = false;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        copied = true;
      }
    } catch {
      // Fall through to the legacy copy path for locally served browsers.
    }

    if (!copied) {
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      copied = document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    if (copied) {
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField((current) => (current === fieldId ? null : current)), 2000);
    }
  };

  return (
    <div className={`card lti-tools-tab__studio-panel lti-tools-tab__studio-panel--${content}`}>
      <div className="card-header lti-tools-tab__studio-panel-header">
        <strong>{content === 'configuration'
          ? intl.formatMessage(messages.studioPanelHeading)
          : intl.formatMessage(messages.reportSectionHeading)}
        </strong>
      </div>
      <div className="card-body">
        {loading && <p className="lti-tools-tab__muted-text">Loading…</p>}
        {error && <div className="alert alert-danger">{error}</div>}
        {block && content === 'configuration' && (
          <>
            <p className="lti-tools-tab__panel-description">{intl.formatMessage(messages.studioPanelDescription)}</p>

            <CopyableConfigField
              label={intl.formatMessage(messages.studioPanelToolUrlLabel)}
              value={block.tool_url}
              fieldId="tool-url"
              isCopied={copiedField === 'tool-url'}
              onCopy={handleCopy}
            />
            <CopyableConfigField
              label={intl.formatMessage(messages.studioPanelConsumerKeyLabel)}
              value={block.consumer_key}
              fieldId="consumer-key"
              isCopied={copiedField === 'consumer-key'}
              onCopy={handleCopy}
            />
            <CopyableConfigField
              label={intl.formatMessage(messages.studioPanelCustomParamsLabel)}
              value={`["${block.custom_parameters}"]`}
              fieldId="custom-parameters"
              isCopied={copiedField === 'custom-parameters'}
              onCopy={handleCopy}
              code
            />
            <div className="lti-tools-tab__field lti-tools-tab__field--last">
              <div className="form-text">Paste this into the <em>Custom Parameters</em> field in Open edX Studio.</div>
            </div>
          </>
        )}
        {block && content === 'report' && (
          <>
            <p className="lti-tools-tab__panel-description">{intl.formatMessage(messages.reportSectionDescription)}</p>

            <CopyableConfigField
              label={intl.formatMessage(messages.reportSectionToolUrlLabel)}
              value={block.report_tool_url}
              fieldId="report-tool-url"
              isCopied={copiedField === 'report-tool-url'}
              onCopy={handleCopy}
            />
            <CopyableConfigField
              label={intl.formatMessage(messages.studioPanelConsumerKeyLabel)}
              value={block.report_consumer_key}
              fieldId="report-consumer-key"
              isCopied={copiedField === 'report-consumer-key'}
              onCopy={handleCopy}
            />
            <CopyableConfigField
              label={intl.formatMessage(messages.studioPanelCustomParamsLabel)}
              value="[]"
              fieldId="report-custom-parameters"
              isCopied={copiedField === 'report-custom-parameters'}
              onCopy={handleCopy}
              code
            />
            <div className="lti-tools-tab__field lti-tools-tab__field--last">
              <div className="form-text">
                No custom parameters required — the LMS automatically passes learner and course context on launch.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StudioBlockPanel;
